import { Table } from 'legacy/lut/library/list'
import { FC, useCallback, useMemo, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { ConfirmationDialog } from 'partner360/components/confirmationDialog/ConfirmationDialog'

import {
  useDeleteEspRequestMutation,
  useGetEspOverviewQuery,
  useResolveEspRequestMutation,
} from '../../api/espApi'
import { ESP_TRANSLATION_KEYS } from '../../configs/translations'
import { toggleEspStatusFilter } from '../../lib/toggleEspStatusFilter'
import { IEspRequest, TEspRequestStatus } from '../../model/types'
import { useConfirmation } from '../hooks/useConfirmation'

import { EspStatusFilter } from './components/espStatusFilter/EspStatusFilter'
import { EspRejectDialog } from './espRejectDialog/EspRejectDialog'
import { EspRequestDetail } from './espRequestDetail/EspRequestDetail'
import { getColumns } from './helpers/getColumns'

import styles from './EspAdminRequestsPanel.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.admin

/**
 * Program Admin page widget: the requests queue with the selected request's detail card
 * below it (wireframe screen 3). The first request is selected by default; approve /
 * reject-with-reason / delete act on the selection from the detail header. Self-contained
 * (fetches its own data), registered as the `jbp-esp-admin-requests` viewModel.
 */
export const EspAdminRequestsPanel: FC = () => {
  const { data: overview, isLoading } = useGetEspOverviewQuery()
  const requests = overview?.requests
  const [resolveRequest, { isLoading: isResolving }] = useResolveEspRequestMutation()
  const [deleteRequest] = useDeleteEspRequestMutation()

  const approveConfirmation = useConfirmation()
  const deleteConfirmation = useConfirmation()
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<TEspRequestStatus[]>([])

  const allRequests = useMemo(() => requests ?? [], [requests])

  // Counts come from every request, so the chips keep showing the full picture while the
  // filter narrows the table.
  const statusCounts = useMemo(
    () =>
      allRequests.reduce(
        (counts, request) => ({ ...counts, [request.status]: counts[request.status] + 1 }),
        { pending: 0, approved: 0, rejected: 0 } as Record<TEspRequestStatus, number>
      ),
    [allRequests]
  )

  const items = useMemo(
    () =>
      statusFilter.length
        ? allRequests.filter((request) => statusFilter.includes(request.status))
        : allRequests,
    [allRequests, statusFilter]
  )

  // The selection is derived, not synced: state only remembers the user's explicit click,
  // and the fallback is the first visible request — so whenever the filtered list is
  // non-empty, one row is always selected and the detail card matches something on screen.
  const selectedRequest = useMemo(() => {
    if (!items.length) {
      return null
    }

    return items.find((request) => request.id === selectedRequestId) ?? items[0]
  }, [items, selectedRequestId])

  const handleStatusToggle = useCallback((status: TEspRequestStatus) => {
    setStatusFilter((current) => toggleEspStatusFilter(current, status))
  }, [])

  const handleApprove = useCallback(async () => {
    if (!selectedRequest) {
      return
    }

    if (await approveConfirmation.confirm()) {
      resolveRequest({ requestId: selectedRequest.id, decision: 'approved' })
    }
  }, [approveConfirmation, resolveRequest, selectedRequest])

  const handleDelete = useCallback(async () => {
    if (!selectedRequest) {
      return
    }

    if (await deleteConfirmation.confirm()) {
      deleteRequest({ requestId: selectedRequest.id })
    }
  }, [deleteConfirmation, deleteRequest, selectedRequest])

  const handleRejectSubmit = useCallback(
    async (reason: string) => {
      if (!selectedRequest) {
        return
      }

      try {
        await resolveRequest({
          requestId: selectedRequest.id,
          decision: 'rejected',
          rejectionReason: reason,
        }).unwrap()

        setIsRejectDialogOpen(false)
      } catch {
        // the api layer already surfaced the error toast; keep the dialog open
      }
    },
    [resolveRequest, selectedRequest]
  )

  const columns = useMemo(() => getColumns(selectedRequest?.id ?? null), [selectedRequest?.id])

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading} />

      <ConfirmationDialog
        {...approveConfirmation.dialogProps}
        title={KEYS.approveConfirmTitle}
        confirmBtnText={KEYS.approveConfirmBtn}
        rejectBtnText={KEYS.confirmCancelBtn}
        content={<Translation text={KEYS.approveConfirmMessage} />}
      />
      <ConfirmationDialog
        {...deleteConfirmation.dialogProps}
        title={KEYS.deleteConfirmTitle}
        confirmBtnText={KEYS.deleteConfirmBtn}
        rejectBtnText={KEYS.confirmCancelBtn}
        isApplyNegative
        content={<Translation text={KEYS.deleteConfirmMessage} />}
      />
      <EspRejectDialog
        show={isRejectDialogOpen}
        isSubmitting={isResolving}
        onSubmit={handleRejectSubmit}
        onCancel={() => setIsRejectDialogOpen(false)}
      />

      <div className={styles.pageTitle}>
        <Translation text={KEYS.pageTitle} span />
      </div>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>
            <Translation text={KEYS.tableTitle} span />
          </span>

          <EspStatusFilter
            counts={statusCounts}
            selected={statusFilter}
            onToggle={handleStatusToggle}
          />
        </div>

        {items.length ? (
          <div className={styles.tableWrap}>
            <Table<IEspRequest>
              items={items}
              keyField="id"
              header
              columns={columns}
              // The table pages by 20 rows by default and shows no pager here: one page holds every request.
              pageSize={1000}
              classes={{ body: styles.tableBody }}
              selectedRowClassName={styles.selectedRow}
              onClick={(item) => setSelectedRequestId(item.id)}
            />
          </div>
        ) : (
          <div className={styles.empty}>
            <Translation text={allRequests.length ? KEYS.emptyFiltered : KEYS.empty} span />
          </div>
        )}
      </section>

      <section className={styles.card}>
        {selectedRequest ? (
          <EspRequestDetail
            request={selectedRequest}
            isResolving={isResolving}
            onApprove={handleApprove}
            onReject={() => setIsRejectDialogOpen(true)}
            onDelete={handleDelete}
          />
        ) : (
          <div className={styles.empty}>
            <Translation text={KEYS.detailEmpty} span />
          </div>
        )}
      </section>
    </div>
  )
}

EspAdminRequestsPanel.displayName = 'EspAdminRequestsPanel'
